import _ from 'lodash'
import instrument from '@adc/instrument'
import type {
  PartyPhone,
  Party,
  Maybe,
  SavedSearch,
  UpdateSearchInput,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import * as blueprint from '@adc/blueprint'
import logger from '@adc/logger'
import { useSelector } from 'react-redux'
import type { AuthenticatedAuctionUser } from '@adc/blueprint/lib/primitives'
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  ChangeEvent,
} from 'react'
import {
  Modal,
  InputField,
  PhoneVerification,
  Button,
  Form,
  Text,
  CheckboxField,
  constants,
  useApplicationNotifications,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import { getDefaultSaveSearchNameFromFilters } from '../../../converters/filters'
import { deleteSearch, updateSearchRedesign } from '../../../providers/search'
import {
  ONE_CLICK_SAVE_SEARCH_CLOSE,
  ONE_CLICK_SAVE_SEARCH_REMOVE,
  ONE_CLICK_SAVE_SEARCH_UPDATE_NAME,
  trackSaveSearch,
} from '../../../services/analytics'
import styles from './styles.scss'
import {
  getModalVersionForAnalytics,
  getSaveSearchParametersFromBlueprintData,
  SavedSearchModalProps,
} from './utils'

const cx = classnames.bind(styles)

const GLOBAL_SMS_OPT_IN_CONSENT_MESSAGE =
  'By checking this box, You are signing your written consent to receive communications from Auction.com and its products like SmartSale, via text and phone at the number you provided and email, including by using an automated dialer or prerecorded or artificial message, for marketing and commercial purposes. Consent is not a condition of purchase. Message frequency varies. You may opt-out at any time. Message and data rates may apply.'
const SAVED_SEARCH_SMS_OPT_IN_CONSENT_MESSAGE =
  'Sign up for SMS notifications for this saved search.'

interface SavedSearchFormProps {
  filtersData: SavedSearchModalProps['data']
  userSavedSearchData: AuthenticatedAuctionUser
  currentSavedSearch: Maybe<SavedSearch>
  onModalClose: () => void
  updateUserSavedSearchData: (user: AuthenticatedAuctionUser) => void
  openedAt?: number
}

const getNewPhones = (
  updatedPhone: PartyPhone,
  userSavedSearchData: SavedSearchFormProps['userSavedSearchData']
) => {
  let newPhones: Party['phones'] = []

  const existingPhone = userSavedSearchData.party?.phones?.find(
    p => p?.phone_id === updatedPhone.phone_id
  )

  if (existingPhone) {
    newPhones = userSavedSearchData.party?.phones?.map(p =>
      p?.phone_id === updatedPhone.phone_id ? updatedPhone : p
    )
  } else {
    newPhones = [updatedPhone, ...(userSavedSearchData.party?.phones ?? [])]
  }

  return newPhones
}

export const SaveSearchForm: React.FC<SavedSearchFormProps> = ({
  filtersData,
  userSavedSearchData,
  currentSavedSearch,
  onModalClose,
  updateUserSavedSearchData,
  openedAt,
}) => {
  const notifications = useApplicationNotifications()
  const redesignEnabled = useSelector(
    (state: { featureFlags?: Record<string, unknown> }) =>
      !!state.featureFlags?.['fe.resi.enable-notification-settings-redesign']
  )
  const { currentFilters } = filtersData
  const initialSavedSearchName = getDefaultSaveSearchNameFromFilters(
    currentFilters
  ) as string
  const [savedSearchName, setSavedSearchName] = useState(initialSavedSearchName)
  const [showError, setShowError] = useState(false)
  const [primaryButtonText, setPrimaryButtonText] = useState('Done')

  const initialLatestPhone = PhoneVerification.utils.getLatestPhone(
    userSavedSearchData.party?.phones ?? []
  ) as Maybe<PartyPhone>

  const [isSavedSearchOptIn, setIsSavedSearchOptIn] = useState(
    currentSavedSearch?.sms_notifications_enabled
  )

  const [latestPhone, setLatestPhone] = useState(initialLatestPhone)

  const isSearchNameChanged = useCallback(
    (currentSearchName: string) =>
      _.trim(currentSearchName) !== _.trim(initialSavedSearchName) &&
      !_.isEmpty(_.trim(currentSearchName)),
    [initialSavedSearchName]
  )

  const [isUpdating, setIsUpdating] = useState(false)

  const communicationPreferenceBuyerSmsOptIn = useMemo(
    () =>
      userSavedSearchData.communication_preference?.rules?.find(
        r =>
          r?.channel === graph.enums.CommunicationPreferenceChannel.Sms &&
          r?.audience === graph.enums.Audience.Buyer
      ),
    [userSavedSearchData.communication_preference?.rules]
  )

  const handleSaveSearchNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newSearchName = e.target.value
    setSavedSearchName(newSearchName)
    setShowError(!_.trim(newSearchName))
  }

  const updateSavedSearch = useCallback(async () => {
    const [, apiValue] = getSaveSearchParametersFromBlueprintData(filtersData)
    if (!currentSavedSearch) {
      return
    }

    setIsUpdating(true)

    const updateSaveSearchInput: UpdateSearchInput = {
      parameters: apiValue,
      saved_search_id: currentSavedSearch.saved_search_id,
      saved_search_name: savedSearchName,
      sms_notifications_enabled: currentSavedSearch.sms_notifications_enabled,
    }

    if (isSavedSearchOptIn) {
      updateSaveSearchInput.sms_notifications_enabled = true
    }

    let newUserSavedSearchData = {}
    let updatedLatestPhone = latestPhone

    try {
      if (
        isSavedSearchOptIn &&
        latestPhone?.number &&
        latestPhone?.verified &&
        !latestPhone?.opt_in // not globally opt in
      ) {
        await blueprint.user.providers.updateOptInInformation({
          phone: latestPhone.number,
          isOptIn: true,
        })

        const newPhones = getNewPhones(
          { ...latestPhone, opt_in: true },
          userSavedSearchData
        )

        updatedLatestPhone = { ...latestPhone, opt_in: true }

        newUserSavedSearchData = {
          ...newUserSavedSearchData,
          party: { ...userSavedSearchData.party, phones: newPhones },
        }
      }

      if (
        isSavedSearchOptIn &&
        communicationPreferenceBuyerSmsOptIn?.opt_status !==
          graph.enums.OptStatus.OptedIn
      ) {
        const newRules =
          userSavedSearchData.communication_preference?.rules?.map(r =>
            r &&
            r.channel === graph.enums.CommunicationPreferenceChannel.Sms &&
            r.audience === graph.enums.Audience.Buyer
              ? { ...r, opt_status: graph.enums.OptStatus.OptedIn }
              : r
          )
        await blueprint.communicationPreferences.providers.updateCommunicationPreferences(
          userSavedSearchData.communication_preference?.urn,
          newRules
        )

        const newCommunicationPreferences = {
          ...userSavedSearchData.communication_preference,
          rules: newRules,
        }

        newUserSavedSearchData = {
          ...newUserSavedSearchData,
          communication_preference: newCommunicationPreferences,
        }
      }

      const modalOpenDuration = openedAt ? Date.now() - openedAt : -1
      if (isSavedSearchOptIn || isSearchNameChanged(savedSearchName)) {
        const savedSearch = (await updateSearchRedesign(
          updateSaveSearchInput
        )) as SavedSearch

        newUserSavedSearchData = {
          ...newUserSavedSearchData,
          saved_searches: userSavedSearchData.saved_searches?.map(s =>
            s?.saved_search_id === savedSearch.saved_search_id ? savedSearch : s
          ),
        }

        notifications.overlayBanner.open({
          title: 'Search Updated',
        })

        if (isSavedSearchOptIn) {
          trackSaveSearch({
            event: ONE_CLICK_SAVE_SEARCH_CLOSE as string,
            user: userSavedSearchData,
            savedSearchName: savedSearch?.saved_search_name,
            smsNotificationsEnabled: !!savedSearch?.sms_notifications_enabled,
            phoneVerified: !!updatedLatestPhone?.verified,
            phoneOptIn: !!updatedLatestPhone?.opt_in,
            modalVersion: getModalVersionForAnalytics(userSavedSearchData),
            modalOpenDuration,
          })
        }

        if (isSearchNameChanged(savedSearchName)) {
          trackSaveSearch({
            event: ONE_CLICK_SAVE_SEARCH_UPDATE_NAME as string,
            user: userSavedSearchData,
            savedSearchName: savedSearch?.saved_search_name,
            smsNotificationsEnabled: !!savedSearch?.sms_notifications_enabled,
            phoneVerified: !!updatedLatestPhone?.verified,
            phoneOptIn: !!updatedLatestPhone?.opt_in,
            modalVersion: getModalVersionForAnalytics(userSavedSearchData),
            modalOpenDuration: modalOpenDuration,
          })
        }
      } else {
        // this case if user clicks done button without changing anything
        trackSaveSearch({
          event: ONE_CLICK_SAVE_SEARCH_CLOSE as string,
          user: userSavedSearchData,
          savedSearchName: currentSavedSearch?.saved_search_name,
          smsNotificationsEnabled:
            !!currentSavedSearch?.sms_notifications_enabled,
          phoneVerified: !!updatedLatestPhone?.verified,
          phoneOptIn: !!updatedLatestPhone?.opt_in,
          modalVersion: getModalVersionForAnalytics(userSavedSearchData),
          modalOpenDuration: modalOpenDuration,
        })
      }
    } catch (err) {
      logger.error(err)
      notifications.overlayBanner.open({
        role: 'error',
        title: 'Search Update Failed',
      })
    } finally {
      if (!_.isEmpty(newUserSavedSearchData)) {
        updateUserSavedSearchData({
          ...userSavedSearchData,
          ...newUserSavedSearchData,
        })
      }
      setIsUpdating(false)
      onModalClose()
    }
  }, [
    filtersData,
    communicationPreferenceBuyerSmsOptIn?.opt_status,
    updateUserSavedSearchData,
    userSavedSearchData,
    isSavedSearchOptIn,
    savedSearchName,
    isSearchNameChanged,
    onModalClose,
    latestPhone,
    notifications.overlayBanner,
    currentSavedSearch,
  ])

  const removeSavedSearch = useCallback(async () => {
    const modalOpenDuration = openedAt ? Date.now() - openedAt : -1
    setIsUpdating(true)
    try {
      await deleteSearch({
        saved_search_id: currentSavedSearch?.saved_search_id,
      })

      instrument.increment('search_delete', 1)

      trackSaveSearch({
        event: ONE_CLICK_SAVE_SEARCH_REMOVE as string,
        user: userSavedSearchData,
        savedSearchName: currentSavedSearch?.saved_search_name,
        smsNotificationsEnabled:
          !!currentSavedSearch?.sms_notifications_enabled,
        phoneVerified: !!latestPhone?.verified,
        phoneOptIn: !!latestPhone?.opt_in,
        modalVersion: getModalVersionForAnalytics(userSavedSearchData),
        modalOpenDuration: modalOpenDuration,
      })

      updateUserSavedSearchData({
        ...userSavedSearchData,
        saved_searches: userSavedSearchData.saved_searches?.filter(
          s => s?.saved_search_id !== currentSavedSearch?.saved_search_id
        ),
      })

      notifications.overlayBanner.open({
        title: 'Search Deleted',
      })
    } catch (err) {
      logger.error(err)
    } finally {
      setIsUpdating(false)
      onModalClose()
    }
  }, [
    updateUserSavedSearchData,
    userSavedSearchData,
    currentSavedSearch?.saved_search_id,
    currentSavedSearch?.saved_search_name,
    currentSavedSearch?.sms_notifications_enabled,
    latestPhone?.opt_in,
    latestPhone?.verified,
    notifications.overlayBanner,
    onModalClose,
  ])

  const onSaveSearchOptInChange = useCallback(() => {
    setIsSavedSearchOptIn(!isSavedSearchOptIn)
  }, [isSavedSearchOptIn])

  const onPhoneVerificatinSuccess = (updatedPhone: PartyPhone) => {
    const newPhones = getNewPhones(updatedPhone, userSavedSearchData)
    if (userSavedSearchData.party) {
      const newParty: Party = {
        ...userSavedSearchData.party,
        phones: newPhones,
      }
      updateUserSavedSearchData({ ...userSavedSearchData, party: newParty })
    }
  }

  useEffect(() => {
    setPrimaryButtonText(
      isSearchNameChanged(savedSearchName) || isSavedSearchOptIn
        ? 'Update'
        : 'Done'
    )
  }, [isSavedSearchOptIn, savedSearchName, isSearchNameChanged])

  useEffect(() => {
    setLatestPhone(initialLatestPhone)
  }, [initialLatestPhone])

  const isSavedSearchUpdateButtonDisabled =
    (isSavedSearchOptIn && !latestPhone?.verified) || isUpdating

  const showLegalese =
    !latestPhone?.opt_in ||
    communicationPreferenceBuyerSmsOptIn?.opt_status !==
      graph.enums.OptStatus.OptedIn

  return (
    <div className={cx('modal-body')}>
      <Form data-elm-id='save_search_form'>
        <Text
          size='big'
          color={constants.ColorNames.GrayDark}
          content={
            redesignEnabled
              ? 'We will notify you through email when a new property matches.'
              : 'We will notify you through email when a new property matches. Check the box below to sign up for text notification.'
          }
        />
        <div className={cx('u-mt-3')}>
          <InputField
            label='Name your search'
            elmId='save_search_value'
            error={
              showError ? 'Please enter a name for your search' : undefined
            }
            touched={showError}
            onChange={handleSaveSearchNameChange}
            value={savedSearchName}
          />
        </div>
        <div className={cx('u-mt-3')}>
          {!redesignEnabled && (
            <>
              <CheckboxField
                className={cx('opt-in-checkbox')}
                checked={isSavedSearchOptIn}
                elmId='sms_opt_in'
                label={
                  <Text
                    color='gray'
                    size='tiny'
                    content={
                      showLegalese
                        ? GLOBAL_SMS_OPT_IN_CONSENT_MESSAGE
                        : SAVED_SEARCH_SMS_OPT_IN_CONSENT_MESSAGE
                    }
                  />
                }
                onChange={onSaveSearchOptInChange}
              />
              {showLegalese && isSavedSearchOptIn && (
                <div className={cx('phone-verification')}>
                  <PhoneVerification.PhoneVerificationReduxForm
                    isStrict
                    showOptInHelpText
                    latestPhone={latestPhone}
                    onSuccess={onPhoneVerificatinSuccess}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Form>

      <Modal.Actions className={cx('modal-actions-background')}>
        <Button
          elmId='remove_search_button'
          role='secondary'
          onClick={removeSavedSearch}
          disabled={isUpdating}
        >
          Remove
        </Button>
        <Button
          elmId='save_search_name_button'
          disabled={isSavedSearchUpdateButtonDisabled}
          role='primary'
          onClick={updateSavedSearch}
        >
          {primaryButtonText}
        </Button>
      </Modal.Actions>
    </div>
  )
}
